import React, { useState } from 'react';
import {
  Truck,
  Mail,
  Lock,
  User,
  Phone,
  MapPin,
  FileText,
  ArrowRight,
  Eye,
  EyeOff,
  Building,
  CheckCircle,
  Copy,
  AlertCircle
} from 'lucide-react';
import CountryCodeSelector from '../ui/CountryCodeSelector';
import authService from '../../services/authService';

// Recreated UI components
const Card = ({ className, children }) => (
  <div className={`rounded-xl border bg-card text-card-foreground shadow-lg ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ className, children }) => (
  <div className={`flex flex-col space-y-1.5 p-6 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ className, children }) => (
  <h3 className={`text-2xl font-semibold leading-none tracking-tight ${className}`}>
    {children}
  </h3>
);

const CardDescription = ({ className, children }) => (
  <p className={`text-sm text-muted-foreground ${className}`}>
    {children}
  </p>
);

const CardContent = ({ className, children }) => (
  <div className={`p-6 pt-0 ${className}`}>
    {children}
  </div>
);

const Input = ({ type = 'text', className, ...props }) => (
  <input type={type} className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} {...props} />
);

const Button = ({ className, variant = 'default', size = 'default', ...props }) => {
  const baseClasses = 'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';
  const variantClasses = {
    default: 'bg-primary text-primary-foreground hover:bg-primary/90',
    outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
  };
  const sizeClasses = {
    default: 'h-10 px-4 py-2',
    sm: 'h-9 rounded-md px-3',
    lg: 'h-11 rounded-md px-8',
    icon: 'h-10 w-10',
  };

  return (
    <button className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`} {...props} />
  );
};

const FleetOperatorRegistrationForm = ({ onSuccess, onError, isLoading, setIsLoading }) => {
  const [formData, setFormData] = useState({
    operator_name: '',
    contact_info: '',
    admin_name: '',
    admin_email: '',
    admin_phone: '',
    admin_phone_country_code: '+91', // Default to India
    admin_temporary_password: '',
    confirmPassword: '',
    business_license: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    emergency_contact: '',
    emergency_contact_country_code: '+91', // Default to India
    service_area: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [registrationResult, setRegistrationResult] = useState(null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const generateTemporaryPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({
      ...prev,
      admin_temporary_password: password,
      confirmPassword: password
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.operator_name.trim()) {
      newErrors.operator_name = 'Fleet operator name is required';
    } else if (formData.operator_name.trim().length < 2 || formData.operator_name.trim().length > 100) {
      newErrors.operator_name = 'Fleet operator name must be between 2-100 characters';
    }

    if (!formData.contact_info.trim()) {
      newErrors.contact_info = 'Contact information is required';
    }

    if (!formData.admin_name.trim()) {
      newErrors.admin_name = 'Admin name is required';
    } else if (formData.admin_name.trim().length < 2 || formData.admin_name.trim().length > 50) {
      newErrors.admin_name = 'Admin name must be between 2-50 characters';
    }

    if (!formData.admin_email.trim()) {
      newErrors.admin_email = 'Admin email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_email)) {
      newErrors.admin_email = 'Please enter a valid email address';
    }

    if (!formData.admin_phone.trim()) {
      newErrors.admin_phone = 'Admin phone is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.admin_phone)) {
      newErrors.admin_phone = 'Please enter a valid 10-digit Indian mobile number starting with 6-9';
    }
    
    if (!formData.admin_temporary_password.trim()) {
      newErrors.admin_temporary_password = 'Temporary password is required';
    } else if (formData.admin_temporary_password.length < 6) {
      newErrors.admin_temporary_password = 'Temporary password must be at least 6 characters';
    }
    
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm the temporary password';
    } else if (formData.admin_temporary_password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData.business_license && formData.business_license.trim().length > 0) {
      if (formData.business_license.trim().length < 5 || formData.business_license.trim().length > 50) {
        newErrors.business_license = 'Business license must be between 5-50 characters';
      }
    }

    if (formData.address && formData.address.trim().length > 0) {
      if (formData.address.trim().length < 10 || formData.address.trim().length > 200) {
        newErrors.address = 'Address must be between 10-200 characters';
      }
    }

    if (formData.city && formData.city.trim().length > 0) {
      if (formData.city.trim().length < 2 || formData.city.trim().length > 50) {
        newErrors.city = 'City must be between 2-50 characters';
      }
    }

    if (formData.state && formData.state.trim().length > 0) {
      if (formData.state.trim().length < 2 || formData.state.trim().length > 50) {
        newErrors.state = 'State must be between 2-50 characters';
      }
    }

    // Pincode validation
    if (formData.pincode && formData.pincode.trim().length > 0) {
      if (!/^\d{6}$/.test(formData.pincode.trim())) {
        newErrors.pincode = 'Pincode must be exactly 6 digits';
      }
    }

    // Emergency contact validation
    if (formData.emergency_contact && formData.emergency_contact.trim().length > 0) {
      if (!/^[6-9]\d{9}$/.test(formData.emergency_contact.trim())) {
        newErrors.emergency_contact = 'Please enter a valid 10-digit Indian mobile number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const result = await authService.registerFleetOperator({
        operator_name: formData.operator_name,
        contact_info: formData.contact_info,
        admin_name: formData.admin_name,
        admin_email: formData.admin_email,
        admin_phone: formData.admin_phone.trim(), // ✅ Keep as string
        admin_phone_country_code: formData.admin_phone_country_code, // ✅ Add country code
        admin_password: formData.admin_temporary_password,
        business_license: formData.business_license,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        pincode: parseInt(formData.pincode.trim(), 10), // ✅ Convert to number
        emergency_contact: formData.emergency_contact.trim(), // ✅ Keep as string
        emergency_contact_country_code: formData.emergency_contact_country_code, // ✅ Add country code
        service_area: formData.service_area
      });

      console.log('Fleet operator registration result:', result);

      if (result.success) {
        console.log('Setting registration result with data:', {
          operatorName: result.data.operator.name,
          adminName: result.data.user.name,
          adminEmail: result.data.user.email,
          adminPhone: result.data.user.phone,
          temporaryPassword: formData.admin_temporary_password
        });

        setRegistrationResult({
          success: true,
          data: {
            operatorName: result.data.operator.name,
            adminName: result.data.user.name,
            adminEmail: result.data.user.email,
            adminPhone: result.data.user.phone,
            temporaryPassword: formData.admin_temporary_password
          }
        });
        
        console.log('Registration result set, should show success page');
        onSuccess && onSuccess(result);
      } else {
        throw new Error(result.message || 'Registration failed');
      }
    } catch (error) {
      const errorMessage = error.message || error.response?.data?.message || 'Registration failed. Please try again.';
      setErrors({
        general: errorMessage
      });
      onError && onError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (registrationResult?.success) {
    console.log('Rendering success page with data:', registrationResult);
    return (
      <div className="space-y-4 sm:space-y-6 mx-2 sm:mx-0">
        <Card className="border-green-200 bg-green-50 shadow-xl rounded-xl">
          <CardHeader className="text-center pb-4 sm:pb-6 px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 md:pt-6">
            <div className="flex justify-center mb-3 sm:mb-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-green-900 text-lg sm:text-xl font-bold">Fleet Operator Registered Successfully!</CardTitle>
            <CardDescription className="text-green-700 text-xs sm:text-sm px-2">
              The fleet operator has been registered and can now login with the temporary credentials.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 sm:space-y-6 px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
            {/* Registration Details */}
            <div className="bg-white border border-green-200 rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3">
              <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Registration Details</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                <div>
                  <span className="font-medium text-gray-600">Fleet Operator:</span>
                  <p className="text-gray-900 break-words">{registrationResult.data.operatorName}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Admin Name:</span>
                  <p className="text-gray-900 break-words">{registrationResult.data.adminName}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Admin Email:</span>
                  <p className="text-gray-900 break-all">{registrationResult.data.adminEmail}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Admin Phone:</span>
                  <p className="text-gray-900">{registrationResult.data.adminPhone}</p>
                </div>
              </div>
            </div>

            {/* Login Credentials */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <h4 className="font-semibold text-blue-900 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                <Lock className="w-4 h-4" />
                Temporary Login Credentials
              </h4>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-white border border-blue-200 rounded-lg p-2 sm:p-3 gap-2">
                  <div className="min-w-0 flex-1">
                    <span className="text-xs sm:text-sm font-medium text-gray-600">Email:</span>
                    <p className="text-gray-900 font-mono text-xs sm:text-sm break-all">{registrationResult.data.adminEmail}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigator.clipboard.writeText(registrationResult.data.adminEmail)}
                    className="text-blue-600 border-blue-300 hover:bg-blue-50 text-xs sm:text-sm w-full sm:w-auto"
                  >
                    <Copy className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    Copy
                  </Button>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-white border border-blue-200 rounded-lg p-2 sm:p-3 gap-2">
                  <div className="min-w-0 flex-1">
                    <span className="text-xs sm:text-sm font-medium text-gray-600">Temporary Password:</span>
                    <p className="text-gray-900 font-mono text-xs sm:text-sm break-all">{registrationResult.data.temporaryPassword}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigator.clipboard.writeText(registrationResult.data.temporaryPassword)}
                    className="text-blue-600 border-blue-300 hover:bg-blue-50 text-xs sm:text-sm w-full sm:w-auto"
                  >
                    <Copy className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    Copy
                  </Button>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
              <div className="flex items-start gap-2 sm:gap-3">
                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <h4 className="font-medium text-amber-900 mb-2 text-sm sm:text-base">First Login Instructions</h4>
                  <div className="text-xs sm:text-sm text-amber-800 space-y-1">
                    <p>• The fleet operator admin can login using the email and temporary password above</p>
                    <p>• On first login, they will be prompted to reset their password</p>
                    <p>• After password reset, they'll need to verify with OTP (use bypass code: 123456)</p>
                    <p>• Once verified, they'll access their fleet operator dashboard</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button
                onClick={() => {
                  setRegistrationResult(null);
                  setFormData({
                    operator_name: '',
                    contact_info: '',
                    admin_name: '',
                    admin_email: '',
                    admin_phone: '',
                    admin_temporary_password: '',
                    confirmPassword: '',
                    business_license: '',
                    address: '',
                    city: '',
                    state: '',
                    pincode: '',
                    emergency_contact: '',
                    service_area: ''
                  });
                }}
                variant="outline"
                className="flex-1 text-xs sm:text-sm"
              >
                Register Another Fleet Operator
              </Button>
              <Button
                onClick={() => onSuccess && onSuccess({ redirect: true })}
                className="flex-1 bg-green-600 hover:bg-green-700 text-xs sm:text-sm"
              >
                <span>Return to Dashboard</span>
                <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <Card className="w-full  mx-auto shadow-xl rounded-xl border-b bg-white/80 backdrop-blur-sm mx-2 sm:mx-0">
     
      <CardContent className="px-3 pt-2 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 md:space-y-6">
          {/* General Error Message */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
              <div className="flex items-start">
                <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-400 mt-0.5 flex-shrink-0" />
                <div className="ml-2 sm:ml-3">
                  <p className="text-xs sm:text-sm text-red-800 break-words">{errors.general}</p>
                </div>
              </div>
            </div>
          )}

          {/* Fleet Operator Information */}
          <div className="space-y-3 sm:space-y-4 md:space-y-6">
            <div className="border-b border-gray-200 pb-2 sm:pb-3 md:pb-4">
              <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <Building className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                <span>Fleet Operator Information</span>
              </h3>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2 sm:space-y-3">
                <label htmlFor="operator_name" className="text-xs sm:text-sm font-medium text-gray-700">
                  Fleet Operator Name *
                </label>
                <div className="relative">
                  <Building className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                  <Input
                    id="operator_name"
                    name="operator_name"
                    type="text"
                    placeholder="Enter fleet operator name"
                    value={formData.operator_name}
                    onChange={handleInputChange}
                    className={`pl-10 sm:pl-12 text-xs sm:text-sm ${errors.operator_name ? 'border-red-300 focus:ring-red-500' : ''}`}
                    disabled={isLoading}
                  />
                </div>
                {errors.operator_name && (
                  <p className="text-xs sm:text-sm text-red-600">{errors.operator_name}</p>
                )}
              </div>

              <div className="space-y-2 sm:space-y-3">
                <label htmlFor="contact_info" className="text-xs sm:text-sm font-medium text-gray-700">
                  Contact Information *
                </label>
                <Input
                  id="contact_info"
                  name="contact_info"
                  type="text"
                  placeholder="Contact information"
                  value={formData.contact_info}
                  onChange={handleInputChange}
                  className={`text-xs sm:text-sm ${errors.contact_info ? 'border-red-300 focus:ring-red-500' : ''}`}
                  disabled={isLoading}
                />
                {errors.contact_info && (
                  <p className="text-xs sm:text-sm text-red-600">{errors.contact_info}</p>
                )}
              </div>
            </div>
          </div>

          {/* Admin Information */}
          <div className="space-y-3 sm:space-y-4 md:space-y-6"> 
            <div className="border-b border-gray-200 pb-2 sm:pb-3 md:pb-4">
              <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                <span>Admin Information</span>
              </h3>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2 sm:space-y-3">
                <label htmlFor="admin_name" className="text-xs sm:text-sm font-medium text-gray-700">
                  Admin Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                  <Input
                    id="admin_name"
                    name="admin_name"
                    type="text"
                    placeholder="Enter admin name"
                    value={formData.admin_name}
                    onChange={handleInputChange}
                    className={`pl-10 sm:pl-12 text-xs sm:text-sm ${errors.admin_name ? 'border-red-300 focus:ring-red-500' : ''}`}
                    disabled={isLoading}
                  />
                </div>
                {errors.admin_name && (
                  <p className="text-xs sm:text-sm text-red-600">{errors.admin_name}</p>
                )}
              </div>

              <div className="space-y-2 sm:space-y-3">
                <label htmlFor="admin_email" className="text-xs sm:text-sm font-medium text-gray-700">
                  Admin Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                  <Input
                    id="admin_email"
                    name="admin_email"
                    type="email"
                    placeholder="Enter admin email"
                    value={formData.admin_email}
                    onChange={handleInputChange}
                    className={`pl-10 sm:pl-12 text-xs sm:text-sm ${errors.admin_email ? 'border-red-300 focus:ring-red-500' : ''}`}
                    disabled={isLoading}
                  />
                </div>
                {errors.admin_email && (
                  <p className="text-xs sm:text-sm text-red-600">{errors.admin_email}</p>
                )}
              </div>
            </div>

            {/* Admin Phone Field - Full width outside grid */}
            <div className="space-y-2 sm:space-y-3">
              <label htmlFor="admin_phone" className="text-xs sm:text-sm font-medium text-gray-700">
                Admin Phone *
              </label>
              <div className="flex">
                <div className="w-20">
                  <CountryCodeSelector
                    value={formData.admin_phone_country_code}
                    onChange={(e) => handleInputChange({ target: { name: 'admin_phone_country_code', value: e.target.value } })}
                    disabled={isLoading}
                    placeholder="Country"
                  />
                </div>
                <div className="flex-1 relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="admin_phone"
                    name="admin_phone"
                    type="tel"
                    placeholder="Enter admin phone number"
                    value={formData.admin_phone}
                    onChange={handleInputChange}
                    className={`pl-10 ${errors.admin_phone ? 'border-red-300 focus:ring-red-500' : ''}`}
                    disabled={isLoading}
                  />
                </div>
              </div>
              {errors.admin_phone && (
                <p className="text-xs sm:text-sm text-red-600">{errors.admin_phone}</p>
              )}
            </div>

            {/* Temporary Password Field - Moved outside the grid */}
            <div className="space-y-2 sm:space-y-3">
              <label htmlFor="admin_temporary_password" className="text-xs sm:text-sm font-medium text-gray-700">
                Temporary Password *
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="admin_temporary_password"
                    name="admin_temporary_password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Set temporary password"
                    value={formData.admin_temporary_password}
                    onChange={handleInputChange}
                    className={`pr-10 ${errors.admin_temporary_password ? 'border-red-300 focus:ring-red-500' : ''}`}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  type="button"
                  onClick={generateTemporaryPassword}
                  variant="outline"
                  className="whitespace-nowrap"
                  disabled={isLoading}
                >
                  Generate
                </Button>
              </div>
              {errors.admin_temporary_password && (
                <p className="text-xs sm:text-sm text-red-600">{errors.admin_temporary_password}</p>
              )}
              <p className="text-xs text-amber-600">
                ⚠️ This is a temporary password. The user will be required to change it on first login.
              </p>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2 sm:space-y-3">
              <label htmlFor="confirmPassword" className="text-xs sm:text-sm font-medium text-gray-700">
                Confirm Temporary Password *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm temporary password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`pl-10 sm:pl-12 pr-10 sm:pr-12 text-xs sm:text-sm ${errors.confirmPassword ? 'border-red-300 focus:ring-red-500' : ''}`}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={isLoading}
                >
                  {showConfirmPassword ? <EyeOff className="w-3 h-3 sm:w-4 sm:h-4" /> : <Eye className="w-3 h-3 sm:w-4 sm:h-4" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-xs sm:text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>
          </div>

          {/* Business Information */}
          <div className="space-y-3 sm:space-y-4 md:space-y-6">
            <div className="border-b border-gray-200 pb-2 sm:pb-3 md:pb-4">
              <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                <span>Business Information (Optional)</span>
              </h3>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2 sm:space-y-3">
                <label htmlFor="business_license" className="text-xs sm:text-sm font-medium text-gray-700">
                  Business License
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                  <Input
                    id="business_license"
                    name="business_license"
                    type="text"
                    placeholder="Business license number"
                    value={formData.business_license}
                    onChange={handleInputChange}
                    className={`pl-10 sm:pl-12 text-xs sm:text-sm ${errors.business_license ? 'border-red-300 focus:ring-red-500' : ''}`}
                    disabled={isLoading}
                  />
                </div>
                {errors.business_license && (
                  <p className="text-xs sm:text-sm text-red-600">{errors.business_license}</p>
                )}
              </div>

              <div className="space-y-2 sm:space-y-3">
                <label htmlFor="emergency_contact" className="text-xs sm:text-sm font-medium text-gray-700">
                  Emergency Contact
                </label>
                <div className="flex">
                  <div className="w-20">
                    <CountryCodeSelector
                      value={formData.emergency_contact_country_code}
                      onChange={(e) => handleInputChange({ target: { name: 'emergency_contact_country_code', value: e.target.value } })}
                      disabled={isLoading}
                      placeholder="Country"
                    />
                  </div>
                  <div className="flex-1 relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="emergency_contact"
                      name="emergency_contact"
                      type="tel"
                      placeholder="Emergency contact number"
                      value={formData.emergency_contact}
                      onChange={handleInputChange}
                      className={`pl-10 ${errors.emergency_contact ? 'border-red-300 focus:ring-red-500' : ''}`}
                      disabled={isLoading}
                    />
                  </div>
                </div>
                {errors.emergency_contact && (
                  <p className="text-xs sm:text-sm text-red-600">{errors.emergency_contact}</p>
                )}
              </div>
            </div>

            <div className="space-y-2 sm:space-y-3">
              <label htmlFor="address" className="text-xs sm:text-sm font-medium text-gray-700">
                Address
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 sm:left-4 top-3 w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                <textarea
                  id="address"
                  name="address"
                  rows={3}
                  placeholder="Enter complete address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={`pl-10 sm:pl-12 w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent resize-none text-xs sm:text-sm ${errors.address ? 'border-red-300 focus:ring-red-500' : ''}`}
                  disabled={isLoading}
                />
              </div>
              {errors.address && (
                <p className="text-xs sm:text-sm text-red-600">{errors.address}</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <div className="space-y-2 sm:space-y-3">
                <label htmlFor="city" className="text-xs sm:text-sm font-medium text-gray-700">
                  City
                </label>
                <Input
                  id="city"
                  name="city"
                  type="text"
                  placeholder="City"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={`text-xs sm:text-sm ${errors.city ? 'border-red-300 focus:ring-red-500' : ''}`}
                  disabled={isLoading}
                />
                {errors.city && (
                  <p className="text-xs sm:text-sm text-red-600">{errors.city}</p>
                )}
              </div>

              <div className="space-y-2 sm:space-y-3">
                <label htmlFor="state" className="text-xs sm:text-sm font-medium text-gray-700">
                  State
                </label>
                <Input
                  id="state"
                  name="state"
                  type="text"
                  placeholder="State"
                  value={formData.state}
                  onChange={handleInputChange}
                  className={`text-xs sm:text-sm ${errors.state ? 'border-red-300 focus:ring-red-500' : ''}`}
                  disabled={isLoading}
                />
                {errors.state && (
                  <p className="text-xs sm:text-sm text-red-600">{errors.state}</p>
                )}
              </div>

              <div className="space-y-2 sm:space-y-3 sm:col-span-2 lg:col-span-1">
                <label htmlFor="pincode" className="text-xs sm:text-sm font-medium text-gray-700">
                  Pincode
                </label>
                <Input
                  id="pincode"
                  name="pincode"
                  type="text"
                  placeholder="123456"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  className={`text-xs sm:text-sm ${errors.pincode ? 'border-red-300 focus:ring-red-500' : ''}`}
                  disabled={isLoading}
                />
                {errors.pincode && (
                  <p className="text-xs sm:text-sm text-red-600">{errors.pincode}</p>
                )}
              </div>
            </div>

            <div className="space-y-2 sm:space-y-3">
              <label htmlFor="service_area" className="text-xs sm:text-sm font-medium text-gray-700">
                Service Area
              </label>
              <Input
                id="service_area"
                name="service_area"
                type="text"
                placeholder="Service area coverage"
                value={formData.service_area}
                onChange={handleInputChange}
                className={`text-xs sm:text-sm ${errors.service_area ? 'border-red-300 focus:ring-red-500' : ''}`}
                disabled={isLoading}
              />
              {errors.service_area && (
                <p className="text-xs sm:text-sm text-red-600">{errors.service_area}</p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center sm:justify-end pt-4 sm:pt-6">
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 sm:py-2 rounded-lg font-medium transition-colors text-sm sm:text-base"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Registering...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <span>Register Fleet Operator</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default FleetOperatorRegistrationForm;
